import React from 'react';
import { View, StyleSheet, Pressable, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from './Text';
import { Theme } from '../../utils/theme';

export interface InsightListItemProps {
  /** The insight title */
  title: string;
  /** Optional index number to display (1-based) */
  index?: number;
  /** Whether the insight has been read/completed */
  isRead?: boolean;
  /** Subtitle text (e.g. "2 min listen", book title) */
  subtitle?: string;
  /** Additional info text (e.g. duration) */
  additionalInfo?: string;
  /** Whether to show the number badge (default: true if index provided) */
  showNumberBadge?: boolean;
  /** Custom right icon component (default: chevron-forward) */
  rightIcon?: React.ReactNode;
  /** Press handler */
  onPress?: () => void;
  /** Whether this is the last item (affects bottom border) */
  isLast?: boolean;
  /** Additional styles for the container */
  style?: ViewStyle;
}

/**
 * Shared insight list item component used across Home, Book Detail, and Saved screens.
 * Provides consistent styling matching the improved design.
 */
const InsightListItem: React.FC<InsightListItemProps> = ({
  title,
  index,
  isRead = false,
  subtitle,
  additionalInfo,
  showNumberBadge = index !== undefined,
  rightIcon,
  onPress,
  isLast = false,
  style,
}) => {
  const renderLeftBadge = () => {
    if (!showNumberBadge) return null;

    if (isRead) {
      return (
        <View style={[styles.badge, styles.badgeRead]}>
          <Ionicons name="checkmark" size={14} color="#fff" />
        </View>
      );
    }

    if (index !== undefined) {
      return (
        <View style={styles.badge}>
          <Text style={styles.badgeNumber}>{index}</Text>
        </View>
      );
    }

    return null;
  };

  const renderRightIcon = () => {
    if (rightIcon !== undefined) {
      return rightIcon;
    }

    return (
      <Ionicons 
        name="chevron-forward" 
        size={20} 
        color={Theme.colors.textSecondary} 
        style={styles.chevron}
      />
    );
  };

  return (
    <Pressable
      style={({ pressed }) => [
        styles.container,
        !isLast && styles.containerWithBorder,
        pressed && styles.containerPressed,
        style,
      ]}
      onPress={onPress}
    >
      <View style={styles.content}>
        {/* Left Badge */}
        {showNumberBadge && (
          <View style={styles.leftBadge}>
            {renderLeftBadge()}
          </View>
        )}

        {/* Text Content */}
        <View style={styles.textContainer}>
          <Text style={styles.title} numberOfLines={2}>
            {title}
          </Text>
          {(subtitle || additionalInfo) && (
            <View style={styles.metaRow}>
              {subtitle && (
                <>
                  <Text style={styles.metaText}>{subtitle}</Text>
                  {additionalInfo && <Text style={styles.metaSeparator}>•</Text>}
                </>
              )}
              {additionalInfo && (
                <Text style={styles.metaText}>{additionalInfo}</Text>
              )}
            </View>
          )}
        </View>

        {/* Right Icon */}
        <View style={styles.rightIcon}>
          {renderRightIcon()}
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'transparent',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.lg,
  },
  containerWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb', // gray-200
  },
  containerPressed: {
    backgroundColor: '#f3f4f6', // gray-100
  },
  content: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Theme.spacing.md,
  },
  leftBadge: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  badge: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#f3f4f6', // gray-100
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeRead: {
    backgroundColor: Theme.colors.primary,
    borderRadius: 4,
  },
  badgeNumber: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#6b7280', // gray-500
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 15,
    lineHeight: 22,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    marginBottom: 6,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
  },
  metaText: {
    fontSize: 12,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.regular,
  },
  metaSeparator: {
    fontSize: 12,
    color: Theme.colors.textSecondary,
  },
  rightIcon: {
    justifyContent: 'center',
    marginTop: 4,
  },
  chevron: {
    flexShrink: 0,
  },
});

export default InsightListItem;

